import { verifyOAuthTokenApi } from "@/api/services/admin/auth";
import { getProductContractHandoffApi } from "@/api/services/admin/product-contract";
import { TProductContractHandoffDataDto } from "@/api/services/admin/product-contract/dtos/get-product-contract-handoff.dto";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { EOrganizationType } from "@/constants/enum";
import { AppRoutes } from "@/routes/config";
import { ADMIN_EMAIL_KEY, ADMIN_USER_ID_KEY, CONTRACT_TOKEN_KEY } from "@/utils/constants";
import { showError } from "@/utils/error";
import { AUTH_COOKIE_EXPIRES_DAYS, removeToken, setToken } from "@/utils/token";
import { getDynamicRoute, withCenterFrom } from "@/utils/url";
import Cookies from "js-cookie";
import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Resolves where a Center handoff lands. The Center only passes the contract state
 * (`contractToken`); building the route is this app's own responsibility.
 *   - referenceProductId set → the organization already exists here → its detail page
 *   - referenceProductId null → not created yet → the matching initial registration page
 * An ambiguous organization type (OTHER) lets the admin pick the type before registering.
 *
 * The initial-registration routes also carry `centerFrom` — the Center pathname the handoff started
 * from, handed over as the `sourcePath` query param on this callback — so that finishing the
 * registration returns the admin to that exact Center screen. Detail routes don't need it: they are
 * a normal landing, not a round trip.
 */
const resolveHandoffRoute = (contract: TProductContractHandoffDataDto | null, sourcePath: string | null): string => {
  if (!contract) return AppRoutes.home;

  const { organizationType, referenceProductId } = contract;

  if (referenceProductId) {
    const id = String(referenceProductId);
    if (organizationType === EOrganizationType.RSO) return getDynamicRoute(AppRoutes.rsoDetail, { id });
    if (organizationType === EOrganizationType.COMPANY) return getDynamicRoute(AppRoutes.companyDetail, { id });
    Cookies.remove(CONTRACT_TOKEN_KEY);
    return AppRoutes.home;
  }

  if (organizationType === EOrganizationType.RSO) return withCenterFrom(AppRoutes.rsoCreate, sourcePath);
  if (organizationType === EOrganizationType.COMPANY) return withCenterFrom(AppRoutes.companyCreate, sourcePath);
  // 種別選択は登録フローの途中なので、ここでも引き継いで作成画面へ渡す。
  if (organizationType === EOrganizationType.OTHER) return withCenterFrom(AppRoutes.organizationTypeSelect, sourcePath);
  return AppRoutes.home;
};

const AdminAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const contractToken = searchParams.get("contractToken");
  // 遷移元のcenterパス。契約の状態ではないので contractToken には含まれず、このURLに直接付いてくる。
  const sourcePath = searchParams.get("sourcePath");

  const clearAuthCookies = useCallback(() => {
    removeToken();
    Cookies.remove(ADMIN_EMAIL_KEY);
    Cookies.remove(ADMIN_USER_ID_KEY);
    Cookies.remove(CONTRACT_TOKEN_KEY);
  }, []);

  const verify = useCallback(async () => {
    // Clear any stale session cookies before starting a fresh OAuth handoff.
    clearAuthCookies();

    if (!token) {
      navigate(AppRoutes.login);
      return;
    }

    try {
      const payload = await verifyOAuthTokenApi(token);

      if (payload.accessToken && payload.refreshToken) {
        setToken(payload.accessToken, payload.refreshToken);
        if (payload.email) {
          Cookies.set(ADMIN_EMAIL_KEY, payload.email, { expires: AUTH_COOKIE_EXPIRES_DAYS });
          Cookies.set(ADMIN_USER_ID_KEY, payload.email, { expires: AUTH_COOKIE_EXPIRES_DAYS });
        }
      }

      // No staged contract on this handoff → plain login.
      if (!contractToken) {
        navigate(AppRoutes.home);
        return;
      }

      // Persist the contract token so the create/list pages can resolve the contract too.
      Cookies.set(CONTRACT_TOKEN_KEY, contractToken);

      const res = await getProductContractHandoffApi({ token: contractToken });
      navigate(resolveHandoffRoute(res.data.data, sourcePath));
    } catch (err) {
      showError(err);
      setTimeout(() => navigate(AppRoutes.login), 2000);
    }
  }, [token, contractToken, sourcePath, navigate, clearAuthCookies]);

  useEffect(() => {
    verify();
  }, [verify]);

  return <LoadingScreen />;
};

export default AdminAuthCallback;
